import React, { useState, useEffect } from 'react';
import { Box, Container, Typography, IconButton, Stack, Button, useTheme, useMediaQuery, Drawer, ListItemIcon, ListItemText } from '@mui/material';
import { 
  Phone, 
  Email, 
  LocationOn, 
  Login, 
  CleaningServices, 
  Analytics,
  Build,
  LocalShipping as MoveInOut,
  Loop,
  CleanHands,
  Home,
  Hotel,
  BusinessCenter,
  Lightbulb,
  CalendarMonth
} from '@mui/icons-material';
import MenuIcon from '@mui/icons-material/Menu';
import { useAuth } from '../contexts/AuthContext';
import { useTenant } from '../contexts/TenantContext';
import { useServiceCatalog } from '../contexts/ServiceCatalogContext';
import { useNavigate } from 'react-router-dom';
import BookingDialog from './booking/BookingDialog';
import { isHouseproBookingEnabled } from '../utils/featureFlags';
import ListItemButton from '@mui/material/ListItemButton';
import PendingActionsIcon from '@mui/icons-material/PendingActions';
import GroupIcon from '@mui/icons-material/Group';
import PersonIcon from '@mui/icons-material/Person';
import AssignmentIcon from '@mui/icons-material/Assignment';

const TopBar = ({ hideAuth = false }) => {
  const { user, logout } = useAuth();
  const { tenant, loading: tenantLoading } = useTenant();
  const { services, loading: servicesLoading } = useServiceCatalog();
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const [bookingDialogOpen, setBookingDialogOpen] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [onboardingStatus, setOnboardingStatus] = useState(null);

  const handleLogout = () => {
    logout(navigate);
  };

  // Fetch onboarding status for MAID users
  useEffect(() => {
    const fetchOnboardingStatus = async () => {
      if (user && user.role && (typeof user.role === 'object' ? user.role.name : user.role) === 'MAID') {
        try {
          const apiBase = process.env.REACT_APP_API_URL?.replace(/\/$/, '') || '';
          const response = await fetch(`${apiBase}/api/maids/onboarding`, {
            headers: {
              'Authorization': `Bearer ${localStorage.getItem('token')}`
            }
          });
          
          const data = await response.json();
          if (response.ok && data.success) {
            // Extract status from maid profile or default to incomplete
            const status = data.onboardingStatus || 'incomplete';
            setOnboardingStatus(status);
          }
        } catch (error) {
          console.error('Error fetching onboarding status:', error);
          // Default to incomplete if we can't fetch
          setOnboardingStatus('incomplete');
        }
      }
    };

    fetchOnboardingStatus();
  }, [user]);

  // Services data now managed by ServiceCatalogContext

  // Helper function to get onboarding button text
  const getOnboardingButtonText = () => {
    if (!onboardingStatus || onboardingStatus === 'incomplete') {
      return {
        primary: 'Complete Onboarding',
        secondary: 'Finish your application'
      };
    } else if (onboardingStatus === 'pending_review') {
      return {
        primary: 'View Application',
        secondary: 'Under review'
      };
    } else if (onboardingStatus === 'approved') {
      return {
        primary: 'View Application',
        secondary: 'Approved'
      };
    } else {
      return {
        primary: 'Update Application',
        secondary: 'Needs revision'
      };
    }
  };

  const handleBookingComplete = () => {
    setBookingDialogOpen(false);
    // Optionally refresh any booking-related data here
  };

  const handleCloseBookingDialog = () => {
    setBookingDialogOpen(false);
  };


  const handleCleaningTips = () => {
    navigate('/cleaning-tips');
    setMobileMenuOpen(false);
  };

  const handleServiceNavigation = (serviceId) => {
    navigate(`/services/${serviceId}`);
    setMobileMenuOpen(false);
  };

  // Get appropriate icon for each service type
  const getServiceIcon = (serviceName) => {
    const name = serviceName.toLowerCase();
    
    if (name.includes('post-construction') || name.includes('construction')) {
      return Build;
    } else if (name.includes('move') || name.includes('in/out')) {
      return MoveInOut;
    } else if (name.includes('recurring') || name.includes('weekly')) {
      return Loop;
    } else if (name.includes('deep') || name.includes('deep clean')) {
      return CleanHands;
    } else if (name.includes('one time') || name.includes('standard')) {
      return Home;
    } else if (name.includes('vacation') || name.includes('rental') || name.includes('airbnb')) {
      return Hotel;
    } else if (name.includes('office') || name.includes('commercial') || name.includes('business')) {
      return BusinessCenter;
    } else {
      return CleaningServices; // Default fallback
    }
  };

  return (
    <Box
      sx={{
        bgcolor: 'primary.main',
        color: 'common.white',
        py: { xs: 0.7, sm: 1.4 },
        position: { xs: 'static', sm: 'static' },
        top: 0,
        left: 0,
        width: '100%',
        zIndex: 1200,
        boxShadow: 3,
        fontSize: { xs: theme.typography.body2.fontSize, sm: theme.typography.body1.fontSize },
        px: { xs: 1.4, sm: 2.8, md: 4.2 },
        minHeight: { xs: 43, sm: 43 },
      }}
    >
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'row',
            alignItems: 'center',
            justifyContent: 'space-between',
            flexWrap: 'wrap',
            width: '100%',
            maxWidth: 'none',
          }}
        >
          {/* Left side: Enhanced Logo with Home indicator */}
          <Box sx={{ display: 'flex', alignItems: 'center', flexShrink: 0 }}>
            <Box
              sx={{ 
                display: 'flex', 
                alignItems: 'center', 
                cursor: 'pointer',
                p: 1,
                borderRadius: 2,
                transition: 'all 0.2s ease',
                '&:hover': {
                  bgcolor: 'rgba(255, 255, 255, 0.1)',
                  transform: 'scale(1.02)',
                  '& .home-indicator': {
                    opacity: 1
                  }
                }
              }}
              onClick={() => navigate('/')}
            >
              <Box
                component="img"
                src="/images/logo2.jpg"
                alt="5 Fairies Cleaning Logo - Go to Home"
                sx={{ 
                  height: { xs: 35, sm: 49, md: 70, lg: 105, xl: 140}, 
                  width: 'auto',
                  filter: 'drop-shadow(0 2px 4px rgba(0,0,0,0.1))'
                }}
              />
              {/* Home indicator - visible on hover */}
              <Box
                sx={{
                  ml: 1,
                  opacity: 0,
                  transition: 'opacity 0.2s ease'
                }}
                className="home-indicator"
              >
                <Typography 
                  variant="caption" 
                  sx={{ 
                    color: 'gold.main',
                    fontWeight: 600,
                    fontSize: { xs: '0.7rem', sm: '0.8rem' },
                    display: { xs: 'none', sm: 'block' }
                  }}
                >
                  Home
                </Typography>
              </Box>
            </Box>
          </Box>

          {/* Center: Company Info - Hidden on mobile, shown on desktop */}
          {!isMobile && (
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', flex: 1 }}>
              <Stack
                direction="column"
                spacing={1}
                alignItems="center"
                sx={{ textAlign: 'center' }}
              >
                {/* Company Name */}
                <Typography 
                  variant="h3" 
                  sx={{ 
                    fontWeight: theme.typography.h3.fontWeight || 700,
                    fontSize: { xs: '1.3rem', sm: '1.5rem', md: '1.8rem', lg: '2rem' },
                    color: 'gold.main'
                  }}
                >
                  {tenant?.name || tenant?.companyName || '5 Fairies Cleaning'}
                </Typography>
                
                
                {/* Contact Info Row */}
                <Stack direction={isMobile ? 'column' : 'row'} spacing={3} alignItems="center">
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Phone sx={{ fontSize: 16, color: 'gold.main' }} />
                    <Typography
                      component="a"
                      href={`tel:${tenant?.clientSupportPhone || tenant?.contactPhone || ''}`}
                      variant="h6"
                      sx={{
                        fontWeight: theme.typography.h6.fontWeight,
                        fontSize: { xs: '1.6rem', sm: '1.8rem', md: '2rem' },
                        color: 'inherit',
                        textDecoration: 'none',
                        cursor: 'pointer',
                        '&:hover': {
                          textDecoration: 'underline'
                        }
                      }}
                    >
                      {tenant?.clientSupportPhone || tenant?.contactPhone || 'Contact us'}
                    </Typography>
                  </Box>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Email sx={{ fontSize: 16, color: 'gold.main' }} />
                    <Typography
                      component="a"
                      href={`mailto:${tenant?.contactEmail || 'hello@primeshine.com'}`}
                      variant="h6"
                      sx={{
                        fontWeight: theme.typography.h6.fontWeight,
                        fontSize: { xs: '1.6rem', sm: '1.8rem', md: '2rem' },
                        color: 'inherit',
                        textDecoration: 'none',
                        cursor: 'pointer',
                        '&:hover': {
                          textDecoration: 'underline'
                        }
                      }}
                    >
                      {!tenantLoading && (tenant?.contactEmail || 'hello@primeshine.com')}
                    </Typography>
                  </Box>
                </Stack>
                

              </Stack>
            </Box>
          )}

          {/* Right side: Quick Quote Button & Menu */}
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flexShrink: 0 }}>
            
            <IconButton
              color="inherit"
              onClick={() => setMobileMenuOpen(true)}
              sx={{ ml: 1 }}
            >
              <MenuIcon />
            </IconButton>
            <Drawer
              anchor="right"
              open={mobileMenuOpen}
              onClose={() => setMobileMenuOpen(false)}
              PaperProps={{ sx: { width: 240 } }}
            >
              <Box sx={{ p: 2 }}>
                {user ? (
                  <>
                    <Typography variant="subtitle1" sx={{ mb: 1 }}>
                      {user.firstName} {user.lastName}
                      {user.role && (
                        <> ({typeof user.role === 'object' ? user.role.name : user.role})</>
                      )}
                    </Typography>
                    {/* Only show Dashboard button for ADMIN */}
                    {user.role && (typeof user.role === 'object' ? user.role.name : user.role) === 'ADMIN' && (
                      <>
                        <ListItemButton onClick={() => { navigate('/dashboard'); setMobileMenuOpen(false); }}>
                          <ListItemIcon><PendingActionsIcon /></ListItemIcon>
                          <ListItemText primary="Dashboard" />
                        </ListItemButton>
                        <ListItemButton onClick={() => { navigate('/team-management'); setMobileMenuOpen(false); }}>
                          <ListItemIcon><GroupIcon /></ListItemIcon>
                          <ListItemText primary="Team Management" />
                        </ListItemButton>
                        <ListItemButton onClick={() => { navigate('/maid-profile'); setMobileMenuOpen(false); }}>
                          <ListItemIcon><PersonIcon /></ListItemIcon>
                          <ListItemText primary="Service Provider Profile" />
                        </ListItemButton>
                        <ListItemButton onClick={() => { navigate('/analytics'); setMobileMenuOpen(false); }}>
                          <ListItemIcon><Analytics /></ListItemIcon>
                          <ListItemText primary="Analytics Dashboard" />
                        </ListItemButton>
                        <ListItemButton onClick={() => { navigate('/profile'); setMobileMenuOpen(false); }}>
                          <ListItemIcon><PersonIcon /></ListItemIcon>
                          <ListItemText primary="My Profile" />
                        </ListItemButton>
                      </>
                    )}
                    
                    {/* MAID Portal Section */}
                    {user.role && (typeof user.role === 'object' ? user.role.name : user.role) === 'MAID' && (
                      <>
                        <ListItemButton onClick={() => { navigate('/maid-onboarding'); setMobileMenuOpen(false); }}>
                          <ListItemIcon><AssignmentIcon /></ListItemIcon>
                          <ListItemText
                            primary={getOnboardingButtonText().primary}
                            secondary={getOnboardingButtonText().secondary}
                          />
                        </ListItemButton>
                        {/* "My Profile" routes to the maid portal — the admin-only /maid-profile page is for managing other maids */}
                        <ListItemButton onClick={() => { navigate('/maid-portal'); setMobileMenuOpen(false); }}>
                          <ListItemIcon><PersonIcon /></ListItemIcon>
                          <ListItemText primary="My Profile" />
                        </ListItemButton>
                      </>
                    )}

                    {/* CLIENT Section */}
                    {user.role && (typeof user.role === 'object' ? user.role.name : user.role) === 'CLIENT' && (
                      <>
                        <ListItemButton onClick={() => { navigate('/bookings'); setMobileMenuOpen(false); }}>
                          <ListItemIcon><CalendarMonth /></ListItemIcon>
                          <ListItemText primary="My Bookings" />
                        </ListItemButton>
                        <ListItemButton onClick={() => { navigate('/profile'); setMobileMenuOpen(false); }}>
                          <ListItemIcon><PersonIcon /></ListItemIcon>
                          <ListItemText primary="My Profile" />
                        </ListItemButton>
                      </>
                    )}

                    {/* Services Menu Section */}
                    {!servicesLoading && services.length > 0 && (
                      <>
                        <Typography variant="subtitle2" sx={{ mt: 2, mb: 1, px: 2, fontWeight: 600, color: 'text.secondary' }}>
                          Our Services
                        </Typography>
                        {services.map((service) => {
                          const ServiceIcon = getServiceIcon(service.service_name);
                          return (
                            <ListItemButton 
                              key={service.id} 
                              onClick={() => handleServiceNavigation(service.id)}
                              sx={{ pl: 3 }}
                            >
                              <ListItemIcon><ServiceIcon /></ListItemIcon>
                              <ListItemText primary={service.service_name} />
                            </ListItemButton>
                          );
                        })}
                      </>
                    )}
                    
                    <ListItemButton onClick={handleCleaningTips}>
                      <ListItemIcon><Lightbulb /></ListItemIcon>
                      <ListItemText primary="Cleaning Tips" />
                    </ListItemButton>
                    <ListItemButton onClick={() => { handleLogout(); setMobileMenuOpen(false); }}>
                      <ListItemIcon><Login /></ListItemIcon>
                      <ListItemText primary="Sign Out" />
                    </ListItemButton>
                  </>
                ) : (
                  <>
                    <ListItemButton onClick={() => {
                      if (isHouseproBookingEnabled()) {
                        window.open('https://client.housecallpro.com/customer_portal/request-link?token=e6fc997cdc864755a09e98544c8f3edd', '_blank');
                      } else {
                        navigate('/login', { state: { returnTo: '/bookings' } });
                      }
                      setMobileMenuOpen(false);
                    }}>
                      <ListItemIcon><Login /></ListItemIcon>
                      <ListItemText primary="Sign In Portal" />
                    </ListItemButton>
                    
                    {/* Services Menu Section for unauthenticated users */}
                    {!servicesLoading && services.length > 0 && (
                      <>
                        <Typography variant="subtitle2" sx={{ mt: 2, mb: 1, px: 2, fontWeight: 600, color: 'text.secondary' }}>
                          Our Services
                        </Typography>
                        {services.map((service) => {
                          const ServiceIcon = getServiceIcon(service.service_name);
                          return (
                            <ListItemButton 
                              key={service.id} 
                              onClick={() => handleServiceNavigation(service.id)}
                              sx={{ pl: 3 }}
                            >
                              <ListItemIcon><ServiceIcon /></ListItemIcon>
                              <ListItemText primary={service.service_name} />
                            </ListItemButton>
                          );
                        })}
                      </>
                    )}
                    
                    <ListItemButton onClick={handleCleaningTips}>
                      <ListItemIcon><Lightbulb /></ListItemIcon>
                      <ListItemText primary="Cleaning Tips" />
                    </ListItemButton>
                  </>
                )}
              </Box>
            </Drawer>
          </Box>
        </Box>
        {/* Company info and actions shown below on mobile only */}
        {isMobile && (
          <Box
            sx={{
              width: '100%',
              mt: 0,
              pt: 0,
              borderTop: (theme) => `1px solid ${theme.palette.divider}`,
              display: 'flex',
              flexDirection: 'column',
              gap: 0
            }}
          >
            {/* Company Name */}
            <Typography 
              variant="h4" 
              sx={{ 
                fontWeight: theme.typography.h4.fontWeight || 700,
                fontSize: { xs: '1.1rem', sm: '1.3rem' },
                color: 'gold.main',
                textAlign: 'center',
                mb: 1
              }}
            >
              {tenant?.name || tenant?.companyName || '5 Fairies Cleaning'}
            </Typography>
            
            
            {/* Contact Info */}
            <Stack direction={isMobile ? 'column' : 'row'} spacing={0} alignItems="center">
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Typography variant="body2" sx={{ fontWeight: theme.typography.h6.fontWeight, fontSize: '0.7rem' }}>
                  Call or text us
                </Typography>
              </Box>              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Phone sx={{ fontSize: 16, color: 'gold.main' }} />
                <Typography
                  component="a"
                  href={`tel:${tenant?.clientSupportPhone || tenant?.contactPhone || ''}`}
                  variant="body2"
                  sx={{
                    fontWeight: theme.typography.h6.fontWeight,
                    fontSize: '1.4rem',
                    color: 'inherit',
                    textDecoration: 'none',
                    cursor: 'pointer',
                    '&:hover': {
                      textDecoration: 'underline'
                    }
                  }}
                >
                  {tenant?.clientSupportPhone || tenant?.contactPhone || 'Contact us'}
                </Typography>
              </Box>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Email sx={{ fontSize: 16, color: 'gold.main' }} />
                <Typography
                  component="a"
                  href={`mailto:${tenant?.contactEmail || 'missed@primeshine.com'}`}
                  variant="body2"
                  sx={{
                    fontWeight: theme.typography.h6.fontWeight,
                    fontSize: '1.4rem',
                    color: 'inherit',
                    textDecoration: 'none',
                    cursor: 'pointer',
                    '&:hover': {
                      textDecoration: 'underline'
                    }
                  }}
                >
                  {!tenantLoading && (tenant?.contactEmail || 'missed@primeshine.com')}
                </Typography>
              </Box>


            </Stack>
          </Box>
        )}

      {/* Booking Dialog - Only show if hideAuth is false and Housepro feature flag is disabled */}
      {!hideAuth && !isHouseproBookingEnabled() && user && (
        <BookingDialog
          open={bookingDialogOpen}
          onClose={handleCloseBookingDialog}
          onBookingComplete={handleBookingComplete}
        />
      )}
    </Box>
  );
};

export default TopBar; 
